import React, { useRef, useLayoutEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useSpring, animated } from 'react-spring';
import styled from 'styled-components';

const PanelWrapper = styled(animated.div)`
  overflow: hidden;
  border-color: ${({ theme }) =>
    theme.colors.gridFilterGroupDisclosurePanelWrapperBorderColor};
`;

const PanelContent = styled.div`
  padding: ${({ viewport }) =>
    viewport === 'desktop'
      ? '.5rem 0rem 1.75rem .75rem'
      : '1rem 1rem 1.75rem 1rem'};
`;

function AnimatedDisclosurePanel({
  open,
  children,
  panelId,
  viewport = 'desktop',
}) {
  const innerRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    if (!innerRef.current) {
      return;
    }

    // Create a ResizeObserver to watch for content size changes
    const observer = new window.ResizeObserver(() => {
      setContentHeight(innerRef.current.scrollHeight);
    });
    observer.observe(innerRef.current);

    // Set initial height
    setContentHeight(innerRef.current.scrollHeight);

    return () => {
      observer.disconnect();
    };
  }, []);

  const style = useSpring({
    height: open ? contentHeight : 0,
    opacity: open ? 1 : 0,
    config: { tension: 400, friction: 28, clamp: true },
    immediate: false,
  });

  return (
    <PanelWrapper style={style} role="region" aria-hidden={!open} id={panelId}>
      <PanelContent ref={innerRef} viewport={viewport}>
        {children}
      </PanelContent>
    </PanelWrapper>
  );
}

AnimatedDisclosurePanel.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool.isRequired,
  panelId: PropTypes.string.isRequired,
  viewport: PropTypes.oneOf(['desktop', 'mobile']),
};

export default AnimatedDisclosurePanel;
